import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil } from 'rxjs';

import { PageHeaderComponent } from '@shared/components/layout';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { CellEditSaveEvent, TableColumn } from '@shared/components/data-table/data-table.component';
import { NotificationService } from '@shared/services/notification.service';
import { DeleteConfirmDialogComponent } from '../../../user/components/shared/delete-confirm-dialog/delete-confirm-dialog.component';
import { AreaDto, AreaManagementService, UpdateAreaDto } from '../../services/area-management.service';
import { AreaEditorComponent, AreaEditorDialogResult } from '../area-editor/area-editor.component';

@Component({
  selector: 'app-area-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatSlideToggleModule,
    MatTooltipModule,
    PageHeaderComponent,
    TableSurfaceComponent,
  ],
  templateUrl: './area-list.component.html',
  styleUrl: './area-list.component.scss',
})
export class AreaListComponent implements OnInit, OnDestroy {
  columns: TableColumn[] = [
    { key: 'name', header: 'Nombre', hideable: true, visible: true, resizable: true, sortable: true, editable: true, editType: 'text' },
    { key: 'isActive', header: 'Activa', hideable: true, visible: true, resizable: true, sortable: true },
    { key: 'actions', header: 'Acciones', width: '1%', minWidth: '104px', exportable: false },
  ];

  tableData: AreaDto[] = [];
  allAreas: AreaDto[] = [];
  isLoading = false;
  savingCell?: { row: AreaDto; column: string };
  searchTerm = '';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly service: AreaManagementService,
    private readonly dialog: MatDialog,
    private readonly notificationService: NotificationService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadAreas();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  createNew(): void {
    const dialogRef = this.dialog.open(AreaEditorComponent, modalDialogConfig({
      data: {},
      size: 'md',
      layout: 'form',
    }));

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: AreaEditorDialogResult | null | undefined) => {
        if (!result) {
          return;
        }

        this.service.create(result.area)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.notificationService.success('Área creada exitosamente');
              this.loadAreas();
            },
            error: error => this.notificationService.error(error?.error?.message || 'Error al crear el área'),
          });
      });
  }

  editArea(area: AreaDto): void {
    const dialogRef = this.dialog.open(AreaEditorComponent, modalDialogConfig({
      data: { area },
      size: 'md',
      layout: 'form',
    }));

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: AreaEditorDialogResult | null | undefined) => {
        if (!result) {
          return;
        }

        this.updateArea(area, result.area as UpdateAreaDto, 'Área actualizada exitosamente');
      });
  }

  deleteArea(area: AreaDto): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, modalDialogConfig({
      data: {
        title: 'Eliminar área',
        message: `¿Estás seguro de que deseas eliminar el área "${area.name}"?`,
      },
      size: 'sm',
      layout: 'confirm',
      tone: 'danger',
    }));

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) {
          this.service.delete(area.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.notificationService.success('Área eliminada exitosamente');
                this.loadAreas();
              },
              error: error => this.notificationService.error(error?.error?.message || 'Error al eliminar el área'),
            });
        }
      });
  }

  onToolbarSearch(term: string): void {
    this.searchTerm = term;
    this.applyFilter();
  }

  onRowsReordered(event: any): void {
    const ordered = event.data as AreaDto[];
    this.service.updateSortOrder(ordered.map(area => area.id))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.allAreas = [...ordered];
          this.notificationService.success('Orden actualizado exitosamente');
          this.applyFilter();
        },
        error: () => {
          this.notificationService.error('Error al actualizar el orden');
          this.loadAreas();
        },
      });
  }

  onStatusChange(area: AreaDto, isActive: boolean): void {
    this.updateArea(area, { name: area.name, sortOrder: area.sortOrder, isActive }, 'Área actualizada exitosamente');
  }

  onCellEditSave(event: CellEditSaveEvent<AreaDto>): void {
    if (event.column !== 'name') {
      return;
    }

    const name = String(event.newValue ?? '').trim();
    if (!name) {
      this.notificationService.warning('El nombre es requerido');
      return;
    }

    if (name === event.oldValue) {
      return;
    }

    this.savingCell = { row: event.row, column: event.column };
    this.updateArea(event.row, { name, sortOrder: event.row.sortOrder, isActive: event.row.isActive }, 'Área actualizada exitosamente');
  }

  private loadAreas(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.service.getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: areas => {
          this.allAreas = areas;
          this.isLoading = false;
          this.applyFilter();
        },
        error: () => {
          this.notificationService.error('Error al cargar las áreas');
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private updateArea(area: AreaDto, dto: UpdateAreaDto, message: string): void {
    this.service.update(area.id, dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          Object.assign(area, dto);
          this.notificationService.success(message);
          this.savingCell = undefined;
          this.applyFilter();
        },
        error: error => {
          this.notificationService.error(error?.error?.message || 'Error al actualizar el área');
          this.savingCell = undefined;
          this.loadAreas();
        },
      });
  }

  private applyFilter(): void {
    const term = this.searchTerm.trim().toLowerCase();
    this.tableData = term
      ? this.allAreas.filter(area => area.name.toLowerCase().includes(term) || area.code.toLowerCase().includes(term))
      : [...this.allAreas];
    this.cdr.markForCheck();
  }
}
